import { createContext, useMemo } from 'react';
import { Firestore, getFirestore } from 'firebase/firestore';
import { useQuery } from 'react-query';
import { NotificationHistoryQueries } from '@frontend/api-notification-history';
import { customSignIn, useFirestoreApp } from './use-firestore-init';

export interface FirestoreDbQueryResult {
  db: Firestore | null;
  queryData: ReturnType<typeof useQuery>;
  refetchSignInToken: () => void;
}

export const FirestoreDbContext = createContext<FirestoreDbQueryResult | undefined>(undefined);

export const FirestoreDbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const app = useFirestoreApp();
  const db = useMemo(() => getFirestore(app), [app]);

  const firestoreTokenQuery = NotificationHistoryQueries.useFirestoreTokenQuery();

  const firestoreSignInQuery = useQuery({
    queryKey: ['firestore-sign-in'],
    queryFn: async () => {
      if (!firestoreTokenQuery?.data?.token) return;
      const user = await customSignIn(firestoreTokenQuery.data.token, app);
      return user;
    },
    onError: (error) => {
      console.error('Failed to complete firestore custom sign in.', error);
    },
    retry: 3,
    staleTime: Infinity,
    enabled: !!firestoreTokenQuery?.data?.token,
  });

  const contextValue = useMemo<FirestoreDbQueryResult>(() => {
    if (firestoreTokenQuery.isError) {
      return { queryData: firestoreTokenQuery, db: null, refetchSignInToken: firestoreTokenQuery.refetch };
    }

    if (firestoreSignInQuery.isSuccess) {
      return { queryData: firestoreSignInQuery, db, refetchSignInToken: firestoreTokenQuery.refetch };
    }

    return { queryData: firestoreSignInQuery, db: null, refetchSignInToken: firestoreTokenQuery.refetch };
  }, [db, firestoreTokenQuery, firestoreSignInQuery]);

  return <FirestoreDbContext.Provider value={contextValue}>{children}</FirestoreDbContext.Provider>;
};
